import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, ShieldCheck, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { billingCheckout } from "@/lib/api";
import { detectBillingCurrency, PLAN_PRICING } from "@/lib/billing";

const INCLUDED = [
  "Full SpaceCost dashboard",
  "Up to 3 rooms in one project",
  "Estimate vs Actual mode",
  "Editable cost library",
  "Multi-currency rates",
  "Project sharing (View / Edit)",
  "PDF-friendly room report",
];

const EXTRAS = [
  {
    label: "+3 Room Pack",
    price: "R80",
    cadence: "once-off",
    body: "Add 3 more rooms to an existing SpaceCost project. Buy as many as you need — packs stack.",
    icon: Plus,
  },
];

export default function SpaceCostPricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const currency = detectBillingCurrency();
  const pricing = PLAN_PRICING[currency];

  const buyProject = async () => {
    if (!user) {
      navigate("/auth?mode=register&product=spacecost");
      return;
    }
    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/billing/callback`;
      const { authorization_url } = await billingCheckout({
        plan: "single_spacecost",
        currency,
        callback_url,
      });
      window.location.href = authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in" data-theme="spacecost" data-testid="spacecost-pricing">
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <button
            data-testid="pricing-back"
            onClick={() => navigate("/spacecost")}
            className="inline-flex items-center gap-2 text-[13px] text-[#57534E] hover:text-[#1C1C1C]"
          >
            <ArrowLeft className="w-4 h-4" /> SpaceCost
          </button>
          <div className="flex items-baseline gap-2.5">
            <span className="fc-heading text-xl sm:text-2xl font-extrabold tracking-tight text-[#1C1C1C] leading-none">SpaceCost</span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              by MyPlanCost
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="fc-label text-sage-700">Pricing</div>
          <h1 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3 leading-[1.05]">
            One-off. No subscription. No surprises.
          </h1>
          <p className="text-base text-[#57534E] mt-5 leading-relaxed">
            SpaceCost is sold as a single project unlock. Each unlock covers one project with up to 3 rooms.
            Need more rooms in the same project? Buy a +3 Room Pack any time.
          </p>
        </div>

        {/* Primary plan */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-2 fc-card p-8 border-2 border-sage-300 relative overflow-hidden" data-testid="plan-single-spacecost">
            <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-sage-100 border border-sage-200 text-sage-700">
              <Sparkles className="w-3 h-3" /> Most popular
            </div>
            <div className="fc-label text-sage-700">Single SpaceCost project</div>
            <div className="mt-4 flex items-end gap-2">
              <div className="fc-heading text-5xl text-[#1C1C1C]">{pricing.single_spacecost?.label || "R180"}</div>
              <div className="text-[13px] text-[#78716C] mb-2">{pricing.single_spacecost?.cadence || "once-off"}</div>
            </div>
            <p className="text-[14px] text-[#57534E] mt-3 max-w-md leading-relaxed">
              Unlocks one SpaceCost project with up to 3 rooms. Use it for a kitchen + scullery + adjacent bathroom,
              or just one bathroom — it's your call.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] text-[#1C1C1C]" data-testid="plan-features">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-sage-700 mt-[1px] shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                data-testid="plan-buy-btn"
                disabled={loading}
                onClick={buyProject}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-12 px-6 text-[15px] font-medium"
              >
                {loading ? "Redirecting…" : (user ? "Buy now" : "Sign up & buy")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <span className="text-[12px] text-[#78716C] inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout via Paystack · ZAR
              </span>
            </div>
          </div>

          {/* Extras */}
          <div className="space-y-5">
            <div className="fc-card p-6" data-testid="plan-room-pack">
              <div className="w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                <Plus className="w-4 h-4" />
              </div>
              <div className="fc-label text-sage-700 mt-4">+3 Room Pack</div>
              <div className="flex items-end gap-2 mt-1">
                <div className="fc-heading text-3xl text-[#1C1C1C]">{pricing.room_pack?.label || "R80"}</div>
                <div className="text-[12px] text-[#78716C] mb-1">once-off</div>
              </div>
              <p className="text-[13px] text-[#57534E] mt-3 leading-relaxed">
                Add 3 more rooms to a project you already own. Available as an upsell from inside the Rooms tab when
                you hit the cap. Packs stack.
              </p>
            </div>
            <div className="fc-card p-6 bg-sage-50/40 border-sage-200">
              <div className="fc-label text-sage-700">Subscriptions</div>
              <div className="fc-heading text-[15px] mt-1.5">Coming soon</div>
              <p className="text-[12px] text-[#57534E] mt-2 leading-relaxed">
                We're working on Pro &amp; Unlimited tiers for designers, contractors and serial planners. Want early access?
              </p>
              <Button
                data-testid="notify-btn"
                onClick={() => navigate("/pricing#notify")}
                variant="ghost"
                className="text-sage-700 hover:text-sage-800 mt-2 px-0"
              >
                Notify me <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center max-w-xl mx-auto text-[12px] text-[#A8A29E]">
          Prices shown in ZAR. Multi-currency display in your account. SpaceCost is part of the MyPlanCost suite — your account works for FlipCost too.
        </div>
      </main>
    </div>
  );
}
